import { useEffect, useRef } from "react";

/** How many ticket keys the hook remembers per scope before forgetting the oldest. */
const SEEN_LIMIT = 500;

/** The chime key for a ticket — the same key always rings once (`useNotificationSound`'s own dedupe). */
export function ticketChimeKey(ticketKey: string): string {
  return `kitchen-ticket:${ticketKey}`;
}

/**
 * ONE CHIME PER NEW TICKET (T12) — independent of push and of the bell.
 *
 * `keys` are the ticket keys the board shows now (`null` while the board for
 * this `scope` has not arrived yet). The first snapshot of a scope — the page
 * load, or the first read after the cook switched station — is the BASELINE
 * and rings nothing: those tickets were not "new", the screen was. Every later
 * snapshot rings once for each key the scope has never shown. A ticket that
 * leaves and comes back (an undo) does not ring again.
 *
 * `chime` is `chimeSound` from the notifications provider: it keeps its 1 s
 * coalescing, its mute preference and its own per-key dedupe, and it plays
 * only once the start overlay's tap has unlocked audio.
 */
export function useNewTicketChime({
  keys,
  scope,
  chime,
}: {
  keys: readonly string[] | null;
  scope: string;
  chime: ((key: string) => void) | undefined;
}): void {
  const seen = useRef<{ scope: string; keys: Set<string> } | null>(null);
  useEffect(() => {
    if (keys === null) return;
    const current = seen.current;
    if (!current || current.scope !== scope) {
      seen.current = { scope, keys: new Set(keys) };
      return;
    }
    for (const key of keys) {
      if (current.keys.has(key)) continue;
      current.keys.add(key);
      if (current.keys.size > SEEN_LIMIT) {
        current.keys.delete(current.keys.values().next().value as string);
      }
      chime?.(ticketChimeKey(key));
    }
  }, [keys, scope, chime]);
}
